import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { HandResponse, HandService, Status } from "src/swagger/api.client.generated";

/**
 * What asking for a day's hand came back with (FLIP-604). `error` is its own answer: a failed call must
 * never be shown as "no hand", or a screen would offer to create a hand that already exists.
 */
export type HandLookupResult =
  | { state: "hand"; hand: HandResponse }
  | { state: "none" }
  | { state: "error" };

/**
 * Reads the hand for a day through the generated client. The day is the "yyyy-MM-dd" string the API takes:
 * use `DateHelper.getCurrentHandDate()` for today, so every "today's hand" entry shares the 4am app-day rule.
 */
@Injectable({ providedIn: "root" })
export class HandLookupService {
  constructor(private handApi: HandService) {}

  forDate(date: string): Observable<HandLookupResult> {
    return this.handApi.getHand(date).pipe(
      map((response): HandLookupResult => {
        if (response?.resultStatus?.statusCode !== Status.Success) {
          return { state: "error" };
        }
        const hand = response.responseData?.hand;
        return hand ? { state: "hand", hand } : { state: "none" };
      }),
      catchError(() => of<HandLookupResult>({ state: "error" }))
    );
  }
}
